import type { AvailabilityStatus } from '@/lib/api/livres/availabilityStatus';

/**
 * Les trois causes de refus du contrôle de dernière seconde, séparées ligne
 * par ligne. Les ensembles `unverifiedLines`, `unavailable` et `heldByOthers`
 * sont DISJOINTS ; `judgedLines` est le complément de `unverifiedLines`.
 */
export interface AvailabilityPartition {
  /** #742 — lignes que le contrôle n'a pas su juger : aucun verdict. */
  unverifiedLines: AvailabilityStatus[];
  /** Toutes les lignes jugées, quel que soit leur verdict. */
  judgedLines: AvailabilityStatus[];
  /** Indisponibilité CATALOGUE : retrait du panier. */
  unavailable: AvailabilityStatus[];
  /** Verrou tenu par un tiers : refus, l'article RESTE au panier. */
  heldByOthers: AvailabilityStatus[];
}

export function partitionAvailability(
  availability: readonly AvailabilityStatus[],
): AvailabilityPartition {
  // ⚠️ #742 — LA PARTITION VIENT EN TÊTE, AVANT TOUT AUTRE VERDICT.
  //
  // Une ligne que le contrôle n'a pas su juger (`verification_impossible`)
  // ne porte AUCUN verdict : ni `disponible`, ni `retenu`, ni
  // `pour_moi` n'ont pu être établis sur elle. La ranger dans
  // `unavailable` — ce que faisait l'ancien `disponible: false` du
  // serveur — la faisait RETIRER du panier et annonçait au client une
  // œuvre vendue. C'est le cas du 08/09 (livre 81627) : une lecture
  // dégradée le temps d'un appel, et l'article disparaît au moment de
  // finaliser.
  //
  // La ranger dans `heldByOthers` ne vaudrait pas mieux : la
  // doctrine « je ne sais pas ⇒ retenu » de `fetchEtatVerrous` rend
  // volontiers `retenu=true, pour_moi=false`, et le client s'entendrait
  // dire qu'un autre client tient son œuvre — un fait que personne n'a
  // constaté.
  //
  // Elle appartient donc à la TROISIÈME cause, celle de la panne du
  // contrôle (#475, #478) : même modale, même refus, panier INTACT. La
  // seule différence est l'échelle — la panne est ici constatée ligne
  // par ligne au lieu de l'être sur l'appel entier.
  const unverifiedLines = availability.filter(
    (item) => item.verification_impossible,
  );
  const judgedLines = availability.filter(
    (item) => !item.verification_impossible,
  );

  const unavailable = judgedLines.filter((item) => !item.disponible);

  // Les deux ensembles sont DISJOINTS par construction : le catalogue
  // prime, parce que son geste est le plus fort. Une œuvre vendue ET
  // retenue est retirée une fois, avec une seule modale — en empiler une
  // seconde sur le même titre n'apprendrait rien au client.
  const heldByOthers = judgedLines.filter(
    (item) =>
      item.disponible &&
      item.retenu &&
      !item.pour_moi &&
      // `isLockedByCurrent` est plus large que `pour_moi` : il couvre
      // aussi la réservation nominative, faute d'écrivain posant un
      // verrou de motif `reservation`. Sans lui, un client se verrait
      // refuser sa propre réservation.
      !item.isLockedByCurrent,
  );

  return { unverifiedLines, judgedLines, unavailable, heldByOthers };
}
